<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="slipRecipientTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="slipRecipientTitle">Lieferschein erstellen</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="spinner" aria-label="Lädt"></div>
          <div v-else-if="errorMessage" class="error">{{ errorMessage }}</div>

          <!-- Ohne offenen Versand gibt es nichts zu verschicken. -->
          <div v-else-if="recipients.length === 0" class="error">
            Es ist kein Gerät für einen Servicepartner vorbereitet.
            Bitte zuerst über „Weitere Aktionen“ den Versand an Servicepartner durchführen.
          </div>

          <template v-else>
            <p class="recipient-hint">An welchen Servicepartner soll der Lieferschein gehen?</p>
            <div class="list-group">
              <button
                v-for="recipient in recipients"
                :key="recipient.id"
                type="button"
                class="list-group-item list-group-item-action recipient-item"
                @click="onChoose(recipient)"
              >
                <span>{{ recipient.description }}</span>
                <span class="badge bg-secondary rounded-pill">{{ recipient.device_count }}</span>
              </button>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Abbrechen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import { listRecipients, type DeliverySlipRecipient } from '../services/deliverySlips';

/**
 * Auswahl des Empfängers für einen Lieferschein. Zur Wahl stehen nur
 * Servicepartner, für die offene Versände vorliegen — alles andere wäre ein
 * leerer Lieferschein.
 *
 * `open()` löst erst im hidden-Event auf, damit das Folgemodal nicht auf den
 * noch offenen Backdrop trifft.
 */
const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: ((recipient: DeliverySlipRecipient | null) => void) | null = null;
let   chosen: DeliverySlipRecipient | null = null;

const loading      = ref(false);
const errorMessage = ref<string | null>(null);
const recipients   = ref<DeliverySlipRecipient[]>([]);

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

function open(): Promise<DeliverySlipRecipient | null> {
  chosen             = null;
  loading.value      = true;
  errorMessage.value = null;
  recipients.value   = [];
  modal?.show();
  void load();
  return new Promise<DeliverySlipRecipient | null>(resolve => {
    resolver = resolve;
  });
}

async function load(): Promise<void> {
  try {
    recipients.value = await listRecipients();
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Die Empfänger konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

function onChoose(recipient: DeliverySlipRecipient): void {
  chosen = recipient;
  modal?.hide();
}

function onHidden(): void {
  resolver?.(chosen);
  resolver = null;
}

defineExpose({ open });
</script>

<style scoped>
.recipient-hint {
  margin-bottom: 0.75rem;
}

.recipient-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #ced4da;
  border-top-color: #0d6efd;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  color: #dc3545;
}
</style>
